import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IAlarmCentral, IPartnerSlim } from "App/Modules/Models";
import type { IAlarmCentralsRepository, IPartnersRepository } from "App/Modules/Repositories";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import { Button, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { jsonEqual, useDebounce } from "App/ReactComponents/Shared";

type InjectedProps = {
    mmAlarmCentralsRepository: IAlarmCentralsRepository;
    mmPartnersRepository: IPartnersRepository;
};
type CreateAlarmCentralDialogProps = InjectedProps & {
    onClose: () => void;
    onCreateAlarmCentral: () => void;
};

export const CreateAlarmCentralDialog = (props: CreateAlarmCentralDialogProps) => {
    const [alarmCentralName, setAlarmCentralName] = useState("");
    const [alarmCentralNameIsValid, setAlarmCentralNameIsValid] = useState(true);
    const [selectedPartner, setSelectedPartner] = useState<IPartnerSlim>(null);
    const [selectedPartnerIsValid, setSelectedPartnerIsValid] = useState(true);
    const [searchPartner, setSearchPartner] = useState("");
    const [partners, setPartners] = useState<IPartnerSlim[]>(null);
    const [addAlarmCentralLoading, setAddAlarmCentralLoading] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const validateAlarmCentralName = (value) => {
        let isValid = true;

        if (!value) {
            isValid = false;
        }

        setAlarmCentralNameIsValid(isValid);
        return isValid;
    };

    const validateSelectedPartner = (value: IPartnerSlim | null) => {
        let isValid = true;

        if (!value) {
            isValid = false;
        }

        if (
            value &&
            !partners.some((partner) => {
                return partner.id === value.id;
            })
        ) {
            isValid = false;
        }

        setSelectedPartnerIsValid(isValid);
        return isValid;
    };

    const validateAlarmCentral = () => {
        // trigger validate methods before returning value in order to ensure all validations are triggered
        const nameIsValid = validateAlarmCentralName(alarmCentralName);
        const partnerIsValid = validateSelectedPartner(selectedPartner);

        return nameIsValid && partnerIsValid;
    };

    const debouncedAlarmCentralName = useDebounce(alarmCentralName, 300);

    useEffect(() => {
        const subscription = extendWithLoadingAndFail(
            props.mmPartnersRepository.getPartnersIncludingGlobal(),
        ).subscribe(
            (response) => {
                if (!jsonEqual(response.data, partners)) {
                    setPartners(response.data);
                }
            },
            () => {
                setLoadError(true);
            },
        );
        return () => {
            subscription.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (debouncedAlarmCentralName) {
            validateAlarmCentralName(alarmCentralName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedAlarmCentralName]);

    const { t } = useTranslation();

    const handleCreateAlarmCentral = () => {
        const isValid = validateAlarmCentral();

        if (isValid) {
            setAddAlarmCentralLoading(true);
            const alarmCentral = {} as IAlarmCentral;
            alarmCentral.partner = selectedPartner;
            alarmCentral.name = alarmCentralName;

            props.mmAlarmCentralsRepository.addAlarmCentral(alarmCentral).subscribe(
                (response) => {
                    if (!response.hasError) {
                        props.onCreateAlarmCentral();
                        setAddAlarmCentralLoading(false);
                    }
                },
                () => {
                    setAddAlarmCentralLoading(false);
                    setSaveError(true);
                },
            );
        }
    };

    const handleClose = () => {
        props.onClose();
    };

    return (
        <div className="card dialog">
            <form
                className="dialog__content form"
                onChange={() => {
                    setSaveError(false);
                }}
            >
                <div className="dialog__section dialog__section--full">
                    <h1 className="title title--h1 title--no-spacing">{t("Gui.Manage.AlarmCentralCreateNew")}</h1>
                </div>
                <div className="dialog__section dialog__section--full">
                    <div className="form__row">
                        <div className="caption">{t("Gui.Manage.AlarmCentralName")}</div>
                        <TextField
                            required
                            fullWidth
                            id="outlined-basic"
                            variant="outlined"
                            size="small"
                            type="text"
                            helperText={!alarmCentralNameIsValid ? t("Validation.Mixed.Required") : null}
                            error={!alarmCentralNameIsValid}
                            value={alarmCentralName}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setAlarmCentralName(event.target.value)
                            }
                            onBlur={(e) => {
                                validateAlarmCentralName(e.target.value);
                            }}
                        />
                    </div>
                </div>
                <div className="dialog__section dialog__section--full">
                    <div className="form__row">
                        <div className="caption">{t("Gui.Manage.AlarmCentralPartner")}</div>
                        <Autocomplete
                            options={partners || ([] as IPartnerSlim[])}
                            getOptionLabel={(p) => p.name}
                            value={selectedPartner}
                            inputValue={searchPartner}
                            loading={!partners}
                            onChange={(e, value: IPartnerSlim) => setSelectedPartner(value)}
                            onInputChange={(e, value) => setSearchPartner(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    error={!selectedPartnerIsValid}
                                    helperText={!selectedPartnerIsValid ? t("Validation.Mixed.Required") : null}
                                    onBlur={() => {
                                        validateSelectedPartner(selectedPartner);
                                    }}
                                    onChange={() => validateSelectedPartner(selectedPartner)}
                                />
                            )}
                        />
                    </div>
                </div>
                {loadError ? (
                    <div className="dialog__section dialog__section--full dialog__section--noscroll">
                        <div className="notice notice--critical">
                            <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                            <p className="caption">{t("Error.ErrorLoading")}</p>
                        </div>
                    </div>
                ) : null}
                {saveError ? (
                    <div className="dialog__section dialog__section--full dialog__section--noscroll">
                        <div className="notice notice--critical">
                            <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                            <p className="caption">{t("Error.ErrorSaving")}</p>
                        </div>
                    </div>
                ) : null}
                <div className="dialog__section dialog__section--buttons">
                    <Button size="small" color="default" onClick={() => handleClose()}>
                        {t("Gui.Buttons.Cancel")}
                    </Button>
                    <Button
                        disabled={!selectedPartnerIsValid || !alarmCentralNameIsValid || addAlarmCentralLoading}
                        type="submit"
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={(e) => {
                            e.preventDefault();
                            if (validateAlarmCentral) {
                                handleCreateAlarmCentral();
                            }
                        }}
                    >
                        {t("Gui.Buttons.Create")}
                    </Button>
                </div>
            </form>
        </div>
    );
};
